import { hotelReservationTransport } from './hotelReservationTransport';
import { hotelQuoteRestartDraft } from './hotelQuoteRestart';
import { useSearchParams } from 'react-router-dom';
import { useGetCaseById } from '@frontend/features/cases/queries';
import { bookingCaseId } from '@frontend/features/travel-services/bookingCaseContext';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { BookingCaseContextProvider } from '@frontend/features/travel-services/components/BookingCaseContext';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Alert, Box, Button, ListSubheader, MenuItem, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '@frontend/config';
import { client } from '@frontend/features/common/services';
import { HotelAgencyContext, HotelSalesAgencyContext } from './HotelAgencyContext';
import { createHotelSearchTransport } from './hotelSearchTransport';
import { HotelSearchPage } from './HotelSearchPage';

type Context = { id: number; name: string };
export function HotelSearchContextSelector() {
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const [params] = useSearchParams();
  const restartId = params.get('restartQuote');
  const restart = useQuery(['hotel-quote-restart', myProfile?.id, restartId], () => hotelReservationTransport.read(restartId!), {
    enabled: !!myProfile?.id && !!restartId, retry: false, refetchOnWindowFocus: false,
  });
  const restartDraft = restart.data ? hotelQuoteRestartDraft(restart.data) : null;
  const initialCaseId = restart.data?.caseId ?? bookingCaseId(params.get('caseId'));
  const caseQuery = useGetCaseById({ id: initialCaseId ?? undefined });
  const preferenceKey = `hotel-search-agency:${myProfile?.id ?? 'anonymous'}`;
  const [selectedSales, setSelectedSales] = useState<number>();
  const [connectionChosen, setConnectionChosen] = useState(false);
  const [locked, setLocked] = useState(false);
  const contexts = useQuery(
    ['hotel-search-contexts', myProfile?.id],
    async () =>
      (
        await client.get<{ data: Context[]; sales_agencies: number[]; sales_agency_options: Context[] }>(
          'hotels/search-contexts',
        )
      ).data,
    { enabled: !!myProfile?.id, staleTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false },
  );
  const [selected, setSelected] = useState<number>(() => {
    try {
      return Number(sessionStorage.getItem(preferenceKey)) || Number(CONFIG.AGENCY_ID);
    } catch {
      return Number(CONFIG.AGENCY_ID);
    }
  });
  const agency =
    (!connectionChosen && restartDraft ? contexts.data?.data.find((item) => item.id === restart.data?.connectionAgencyId)?.id : undefined) ??
    contexts.data?.data.find((item) => item.id === selected)?.id ??
    contexts.data?.data.find((item) => item.id === Number(CONFIG.AGENCY_ID))?.id ??
    contexts.data?.data[0]?.id;
  const salesOptions = contexts.data?.sales_agency_options ?? [];
  const salesAgency =
    salesOptions.find((x) => x.id === selectedSales)?.id ??
    salesOptions.find((x) => x.id === caseQuery.data?.agencyId)?.id ??
    salesOptions.find((x) => x.id === Number(CONFIG.AGENCY_ID))?.id ??
    salesOptions[0]?.id;
  const transport = useMemo(() => createHotelSearchTransport(agency, salesAgency), [agency, salesAgency]);
  if (!myProfile?.id || contexts.isLoading || (restartId && restart.isLoading) || (initialCaseId && caseQuery.isLoading))
    return <Alert severity="info">{t('hotelConnections.loading')}</Alert>;
  if (contexts.isError)
    return (
      <Alert severity="error">
        {t('hotelConnections.error')}
        <Button onClick={() => contexts.refetch()}>{t('hotelConnections.retry')}</Button>
      </Alert>
    );
  if (!agency) return <Alert severity="info">{t('hotelSharing.noContexts')}</Alert>;
  const connectionSelector = (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <TextField
        select
        size="small"
        label={t('hotelCostMarkup.salesAgency')}
        value={salesAgency ?? ''}
        disabled={locked}
        onChange={(event) => setSelectedSales(Number(event.target.value))}
        sx={{ minWidth: 220, '& .MuiSelect-select': { fontSize: 16 } }}
      >
        {salesOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      <Tooltip title={t('hotelSharing.contextHint')} arrow>
        <Box sx={{ width: { xs: '100%', md: 240 }, maxWidth: '100%', minWidth: 0 }}>
          <TextField
            select
            size="small"
            sx={{
              '& .MuiInputBase-root': { bgcolor: 'background.paper' },
              '& .MuiSelect-select': { fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
            fullWidth
            label={t('hotelSharing.searchWith')}
            value={agency}
            disabled={locked}
            onChange={(event) => {
              const next = Number(event.target.value);
              setConnectionChosen(true);
              setSelected(next);
              try {
                sessionStorage.setItem(preferenceKey, String(next));
              } catch {
                /* optional preference */
              }
            }}
          >
            <ListSubheader sx={{ whiteSpace: 'normal', lineHeight: 1.5, py: 1, maxWidth: 320 }}>
              {t('hotelSharing.contextHint')}
            </ListSubheader>
            {contexts.data?.data.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Tooltip>
    </Box>
  );
  return (
    <HotelAgencyContext.Provider value={agency}>
      <HotelSalesAgencyContext.Provider value={salesAgency}>
        <BookingCaseContextProvider allowedAgencyIds={salesAgency ? [salesAgency] : []}>
          <HotelSearchPage
            key={`${myProfile?.id}:${agency}:${salesAgency}`}
            transport={transport}
            initialDraft={restartDraft ?? undefined}
            restartNotice={restartId ? (restartDraft ? 'hotelReservation.restartNotice' : 'hotelReservation.restartUnavailable') : undefined}
            onContextLockedChange={setLocked}
            headerActions={connectionSelector}
          />
        </BookingCaseContextProvider>
      </HotelSalesAgencyContext.Provider>
    </HotelAgencyContext.Provider>
  );
}
